// Layout / viewport / paper-space reducer cases extracted from
// geometryReducer.ts as part of the PR-0 mechanical decomposition.
//
// This file hosts `geometry/create-viewport`, the Phase MC5
// per-viewport override commands (lock, VPLAYER, VPCLIP, scale) and the
// LAYOUT-COMMAND-HANDLERS space lifecycle actions (`shell/add-space` /
// `shell/remove-space` / `shell/rename-space` — LAYOUT NEW / DELETE /
// RENAME). Future layout/space actions (VPMAX/VPMIN, viewport
// scale-list edit) should also land here so the shell stays under the
// CLAUDE.md 1000-line soft limit.
//
// Returns `null` when the dispatched command is not a layout-shaped
// action, signalling to the chain in `geometryReducer.ts` that the
// next sub-reducer should run.
import {
  assembleDrawingDocument,
  buildDefaultLayout,
  DEFAULT_PAPER_FALLBACK_LAYOUT_NAME,
  isPaperSpaceBlockName,
  type DrawingDocument,
  type Layout,
} from "@dxf-ai-editor/drawing-model";
import type {
  DynamicBlockCommand,
  EditorCommand,
} from "@dxf-ai-editor/contracts";

import {
  appendGeometryEntity,
  isPaperSpaceLayoutTarget,
  nextGeometryEntityId,
} from "./geometryReducerHelpers";

import {
  resolveAdjacentLayoutId,
  validateAddSpace,
  validateRemoveSpace,
  validateRenameSpace,
} from "./spaceMutationGuards";

import { computeViewportEditMutation } from "./viewportEditMutation";

// Tab order: AutoCAD appends new tabs after the last one. Take the max
// over the known LAYOUT objects' tabOrder and the layout-space count
// (covers seed documents whose layouts dict is empty), +1. Shared by
// `shell/add-space` and the `shell/rename-space` materialisation
// fallback (Codex P2 — single source of truth, no clone).
function deriveNextTabOrder(geometryDocument: DrawingDocument): number {
  const knownTabOrders = [
    ...Object.values(geometryDocument.layouts ?? {}),
    ...Object.values(geometryDocument.layoutsBySpaceId ?? {}),
  ]
    .map((layout) => layout.tabOrder)
    .filter((order): order is number => Number.isFinite(order));
  const layoutSpaceCount = geometryDocument.spaces.filter(
    (space) => space.kind === "layout",
  ).length;
  return (
    Math.max(
      layoutSpaceCount,
      ...(knownTabOrders.length ? knownTabOrders : [0]),
    ) + 1
  );
}

// Codex P2-1 (PR #311) — duplicate-name aware LAYOUT entry resolution.
//
// The importer disambiguates the visible `space.name` ("Sheet", "Sheet (1)")
// while the by-name `geometryDocument.layouts` map stays keyed by the RAW
// LAYOUT name — two same-named LAYOUTs collapse to ONE by-name entry (the
// last). `layoutsBySpaceId` is the authoritative per-space binding, so a
// space's by-name entry must be resolved by comparing AGAINST the by-id
// entry — never by blind `layouts[space.name]` lookup, which deletes the
// WRONG (or shared) entry on LAYOUT DELETE / RENAME of a duplicate.
//
// Identity comparison alone is not enough: the importer materialises the
// by-name and by-id entries through SEPARATE `convertRawLayout` calls, so
// the same LAYOUT object yields two distinct JS objects. The discriminator
// chain mirrors the data: LAYOUT handle (group 5 — hex, case-INSENSITIVE
// per the repo-wide normalizeHandle convention), then the owning
// BLOCK_RECORD handle (group 330), then the stored layout name (runtime
// layouts carry empty handles but unique names).
function isSameLayoutEntry(a: Layout, b: Layout): boolean {
  if (a === b) return true;
  const normalizeHandle = (handle: string | undefined): string =>
    (handle ?? "").trim().toLowerCase();
  const aHandle = normalizeHandle(a.handle);
  const bHandle = normalizeHandle(b.handle);
  if (aHandle || bHandle) return aHandle === bHandle;
  const aOwner = normalizeHandle(a.ownerBlockRecordHandle);
  const bOwner = normalizeHandle(b.ownerBlockRecordHandle);
  if (aOwner || bOwner) return aOwner === bOwner;
  return a.name === b.name;
}

// Resolve the by-name `layouts` key that belongs to the given space. The
// by-id entry (when present) is authoritative; a visible-name fallback is
// kept ONLY for legacy documents that never populated `layoutsBySpaceId`
// (hand-built fixtures / pre-import seed docs), where names are the sole
// binding. Returns `undefined` when no by-name entry belongs to the space
// (e.g. the duplicate-name import collapsed it away).
function findLayoutKeyForSpace(
  layouts: Record<string, Layout>,
  byIdEntry: Layout | undefined,
  visibleName: string | undefined,
): string | undefined {
  if (byIdEntry) {
    for (const [key, entry] of Object.entries(layouts)) {
      if (isSameLayoutEntry(entry, byIdEntry)) return key;
    }
    return undefined;
  }
  if (visibleName !== undefined && visibleName in layouts) return visibleName;
  return undefined;
}

// Codex P2-1 (second half) + P2-4 (r5) — re-seed survivors whose by-name
// entry was collapsed away by the duplicate-name import (the by-name map
// held ANOTHER same-named layout's entry under the shared raw key, and
// the delete/rename of that layout just removed the key). Without this
// the surviving tab loses its LAYOUT object on export (`exportLayouts`
// walks the by-name map only). Keyed by the survivor's stored (raw)
// layout name — the same key `importLayouts` would use; first-wins on a
// residual collision. Shared by the `shell/remove-space` delete path and
// the `shell/rename-space` re-key path (one source of truth — the rename
// of a duplicate previously deleted the shared key without re-seeding,
// vanishing the sibling's LAYOUT from export). Mutates `layouts` in
// place (callers pass their already-copied next map).
function reseedSurvivorByNameEntries(
  spaces: DrawingDocument["spaces"],
  excludedSpaceId: string,
  layoutsBySpaceId: Record<string, Layout>,
  layouts: Record<string, Layout>,
): void {
  for (const space of spaces) {
    if (space.id === excludedSpaceId || space.kind !== "layout") continue;
    const survivorEntry = layoutsBySpaceId[space.id];
    if (!survivorEntry) continue;
    const hasByName = Object.values(layouts).some((entry) =>
      isSameLayoutEntry(entry, survivorEntry),
    );
    if (!hasByName && !(survivorEntry.name in layouts)) {
      layouts[survivorEntry.name] = survivorEntry;
    }
  }
}

// Codex P2-3 + P2-2 (r5) — the tab names claimed EXCLUSIVELY by the given
// space: its candidate names (visible space name + raw LAYOUT entry name)
// minus every name a SIBLING layout still claims (visible name or its
// by-id entry's raw name). Raw ENTITIES-section passthrough records are
// keyed to a tab by group 410 = tab name (the key
// `resolveTopLevelEntityDraft` routes by on import), so only the names in
// this set may have their records purged (delete) or retagged (rename) —
// a shared duplicate name still claimed by a survivor belongs to the
// survivor's records. Shared by `shell/remove-space` and
// `shell/rename-space` (one source of truth for the duplicate-safety
// rule).
function collectExclusiveTabNames(
  geometryDocument: DrawingDocument,
  spaceId: string,
  candidateNames: Array<string | undefined>,
): Set<string> {
  const names = new Set<string>();
  for (const name of candidateNames) {
    if (name) names.add(name);
  }
  for (const space of geometryDocument.spaces) {
    if (space.id === spaceId || space.kind !== "layout") continue;
    names.delete(space.name);
    const siblingEntry = (geometryDocument.layoutsBySpaceId ?? {})[space.id];
    if (siblingEntry?.name) names.delete(siblingEntry.name);
  }
  return names;
}

// ReducerMutationResult compatible shape — we reuse DrawingDocument for
// the viewport mutation result (these commands only update one entity).
export function applyLayoutAction(
  geometryDocument: DrawingDocument,
  command: EditorCommand | DynamicBlockCommand,
): DrawingDocument | null {
  // ── Phase MC5 — per-viewport override commands (lock / VPLAYER / VPCLIP set
  // + remove / scale / set-view). The mutated-vs-no-op decision lives in the
  // single source of truth `computeViewportEditMutation` (shared with the
  // shell-audit gate). `result.document` is the same reference as the input doc
  // on a no-op, so returning it directly preserves the prior reducer behaviour.
  const viewportEdit = computeViewportEditMutation(geometryDocument, command);
  if (viewportEdit) return viewportEdit.document;

  switch (command.type) {
    // ── LAYOUT NEW (shell/add-space) — LAYOUT-COMMAND-HANDLERS ──────────
    // Append a real drawing-model paper-space layout (kind="layout",
    // zero entities) AND materialise its LAYOUT page-setup object so the
    // DXF export emits an OBJECTS-section LAYOUT entry — an empty new
    // tab round-trips (AutoCAD: every layout tab owns a LAYOUT object).
    // Guards are the SHARED `validateAddSpace` predicates also evaluated
    // by the shell arm (`shellUiReducer.ts`) so the two dispatch arms
    // accept/reject identically; rejection = silent no-op here (the
    // shell arm owns the warning audit). Does NOT activate the new tab
    // (AutoCAD LAYOUT New stays on the current layout).
    case "shell/add-space": {
      const name = command.name.trim();
      if (
        !name ||
        validateAddSpace(geometryDocument.spaces, {
          spaceId: command.spaceId,
          name,
        }) !== null
      ) {
        return geometryDocument;
      }
      // `buildDefaultLayout` (drawing-model, shared with the importer's
      // synthetic-layout seeding) fills the full page-setup defaults
      // (handle / limits / extents / A4-mm plot settings) AND keeps the
      // legacy flat plot-mirror fields (paperSize / plotPaperUnits /
      // plotRotation / plotOrigin) in sync with the authoritative
      // `plotSettings`. A bare `layoutSchema.parse` left the mirrors at the
      // schema zero-defaults (paperSize="" / plotPaperUnits=0=inches), so
      // `effectivePlotSettings` exported group 72=0 (inches) and
      // `extractPageSetup` read inches for a fresh A4/mm tab (Codex P2-2).
      const newLayout: Layout = buildDefaultLayout(
        name,
        deriveNextTabOrder(geometryDocument),
      );
      return assembleDrawingDocument({
        ...geometryDocument,
        spaces: [
          ...geometryDocument.spaces,
          { id: command.spaceId, kind: "layout" as const, name, entities: [] },
        ],
        layouts: {
          ...(geometryDocument.layouts ?? {}),
          [name]: newLayout,
        },
        layoutsBySpaceId: {
          ...(geometryDocument.layoutsBySpaceId ?? {}),
          [command.spaceId]: newLayout,
        },
      });
    }

    // ── LAYOUT DELETE (shell/remove-space) — LAYOUT-COMMAND-HANDLERS ────
    // Remove the paper-space layout, its entities, and its LAYOUT
    // page-setup objects (by-id and by-name keys) + per-space viewport
    // snapshot. Shared guards (`validateRemoveSpace`: Model protected,
    // last-remaining-layout protected, unknown id) no-op silently here —
    // the shell arm owns the warning audit. When the deleted layout was
    // the document's active space, the adjacent remaining layout becomes
    // active (drawingDocumentSchema requires activeSpaceId to resolve).
    // Cache/index invalidation is downstream: the dispatch pipeline
    // diffs the documents (`diffDocumentMutations` reports every entity
    // of a vanished space as removed) and `commitMutation` drops them.
    case "shell/remove-space": {
      if (
        validateRemoveSpace(geometryDocument.spaces, command.spaceId) !== null
      ) {
        return geometryDocument;
      }
      const removedSpace = geometryDocument.spaces.find(
        (space) => space.id === command.spaceId,
      );
      // Codex P2-1 — resolve the removed space's BY-NAME entry through the
      // by-id binding (`isSameLayoutEntry`), never by blind
      // `layouts[visibleName]`: with duplicate imported LAYOUT names the
      // by-name map is keyed by the RAW name and holds only ONE (the last)
      // entry, while the visible space names are disambiguated ("Sheet",
      // "Sheet (1)") — a visible-name delete removes the WRONG (or shared)
      // entry and corrupts the surviving layout.
      const removedByIdEntry = (geometryDocument.layoutsBySpaceId ?? {})[
        command.spaceId
      ];
      const removedLayoutKey = findLayoutKeyForSpace(
        geometryDocument.layouts ?? {},
        removedByIdEntry,
        removedSpace?.name,
      );
      const removedLayoutEntry =
        removedByIdEntry ??
        (removedLayoutKey !== undefined
          ? (geometryDocument.layouts ?? {})[removedLayoutKey]
          : undefined);
      const nextLayoutsBySpaceId = {
        ...(geometryDocument.layoutsBySpaceId ?? {}),
      };
      delete nextLayoutsBySpaceId[command.spaceId];
      const nextLayouts = { ...(geometryDocument.layouts ?? {}) };
      if (removedLayoutKey !== undefined) {
        delete nextLayouts[removedLayoutKey];
      }
      // Codex P2-1 (second half) — re-seed survivors whose by-name entry
      // was collapsed away by the duplicate-name import (shared helper —
      // the rename path needs the identical re-seed, Codex P2-4 r5).
      reseedSurvivorByNameEntries(
        geometryDocument.spaces,
        command.spaceId,
        nextLayoutsBySpaceId,
        nextLayouts,
      );
      const nextViewportsBySpaceId = {
        ...(geometryDocument.viewportsBySpaceId ?? {}),
      };
      delete nextViewportsBySpaceId[command.spaceId];
      // Codex P2-3 — an IMPORTED layout owns a *Paper_Space<N> BLOCK
      // definition + BLOCK_RECORD, linked via the LAYOUT.330 ↔
      // BLOCK_RECORD.5 handle pair (`layout.ownerBlockRecordHandle`).
      // AutoCAD / ezdxf `Layouts.delete()` discard that block together
      // with the layout; leaving it behind re-emits a dangling
      // paper-space BLOCK on every export. Resolve STRICTLY through the
      // removed layout's own handle pair and only match paper-space
      // block names, so the surviving layouts' *Paper_Space blocks (and
      // *Model_Space) are never touched. Runtime-created layouts carry
      // an empty ownerBlockRecordHandle — nothing to purge.
      let nextBlocks = geometryDocument.blocks;
      let nextBlockRecords = geometryDocument.blockRecords;
      const ownerHandle = removedLayoutEntry?.ownerBlockRecordHandle;
      // Codex P2 — DXF handles are HEX and case-INSENSITIVE: producers
      // mix case between LAYOUT.330 and BLOCK_RECORD.5 (`8a1c` vs
      // `8A1C` denote the SAME handle). Compare via the repo-wide
      // lookup convention (dxf-io `normalizeHandle`: trim+lowercase);
      // stored values keep their original strings (lookup-only).
      const ownerHandleKey = ownerHandle?.trim().toLowerCase();
      const ownedRecordName = ownerHandleKey
        ? Object.entries(geometryDocument.blockRecords ?? {}).find(
            ([recordName, record]) =>
              record.handle?.trim().toLowerCase() === ownerHandleKey &&
              isPaperSpaceBlockName(recordName),
          )?.[0]
        : undefined;
      if (ownedRecordName !== undefined) {
        nextBlockRecords = { ...nextBlockRecords };
        delete nextBlockRecords[ownedRecordName];
        if (ownedRecordName in (geometryDocument.blocks ?? {})) {
          nextBlocks = { ...nextBlocks };
          delete nextBlocks[ownedRecordName];
        }
      }
      // Codex P2-3 — purge raw ENTITIES-section passthrough records of the
      // removed layout. Unsupported top-level entities (ACAD_TABLE, proxy
      // graphics, …) live in `geometryDocument.rawEntityGroups`, NOT in
      // `space.entities` / the paper-space block, and the writer re-emits
      // them verbatim — without this purge the export still carries records
      // whose paper-space assignment (group 410 = layout tab name, the SAME
      // key `resolveTopLevelEntityDraft` routes by on import; group 67=1
      // without 410 = the default `*Paper_Space` layout) points at the
      // deleted tab. Duplicate-name safe: a tab name still claimed by a
      // SURVIVING space (visible name or its by-id entry's raw name) is
      // never purged — those records belong to the survivor.
      const removedTabNames = collectExclusiveTabNames(
        geometryDocument,
        command.spaceId,
        [removedSpace?.name, removedLayoutEntry?.name],
      );
      // The removed layout owns the DEFAULT paper-space records (67=1
      // without 410) iff the importer's no-410 routing chain
      // (`resolveTopLevelEntityDraft`) targets it:
      //   1. explicit owner pair — its BLOCK_RECORD resolved to the
      //      un-suffixed `*Paper_Space` name (computed above for the
      //      block purge);
      //   2. ownerless fallback (Codex P2-1 r5) — when NO layout in the
      //      document claims the `*Paper_Space` record at all (legacy
      //      import carrying 67=1-no-410 entities with no LAYOUT entry /
      //      BLOCK_RECORD owner), the importer routed those entities to
      //      the synthetic `layout:<DEFAULT_PAPER_FALLBACK_LAYOUT_NAME>`
      //      space — deleting THAT space must purge the records even
      //      though `ownedRecordName` resolves undefined. When some
      //      OTHER layout claims `*Paper_Space`, the records belong to
      //      that claimant and an ownerless delete must NOT touch them.
      const defaultPaperRecordHandleKey = (geometryDocument.blockRecords ??
        {})["*Paper_Space"]?.handle
        ?.trim()
        .toLowerCase();
      const defaultPaperSpaceClaimed =
        Boolean(defaultPaperRecordHandleKey) &&
        [
          ...Object.values(geometryDocument.layoutsBySpaceId ?? {}),
          ...Object.values(geometryDocument.layouts ?? {}),
        ].some(
          (entry) =>
            entry.ownerBlockRecordHandle?.trim().toLowerCase() ===
            defaultPaperRecordHandleKey,
        );
      const removedOwnsDefaultPaperSpace =
        ownedRecordName === "*Paper_Space" ||
        (!defaultPaperSpaceClaimed &&
          command.spaceId ===
            `layout:${DEFAULT_PAPER_FALLBACK_LAYOUT_NAME}`);
      let nextRawEntityGroups = geometryDocument.rawEntityGroups;
      if (
        nextRawEntityGroups?.length &&
        (removedTabNames.size > 0 || removedOwnsDefaultPaperSpace)
      ) {
        const purged = nextRawEntityGroups.filter((record) => {
          const tabName = record.groups
            .find((group) => group.code === 410)
            ?.value.trim();
          if (tabName) return !removedTabNames.has(tabName);
          if (!removedOwnsDefaultPaperSpace) return true;
          // No 410 → the record belongs to the default `*Paper_Space`
          // layout when group 67 says paper space (importer fallback B).
          const paperFlag = record.groups.find((group) => group.code === 67);
          return Number(paperFlag?.value.trim() ?? "0") !== 1;
        });
        if (purged.length !== nextRawEntityGroups.length) {
          nextRawEntityGroups = purged;
        }
      }
      return assembleDrawingDocument({
        ...geometryDocument,
        spaces: geometryDocument.spaces.filter(
          (space) => space.id !== command.spaceId,
        ),
        activeSpaceId:
          geometryDocument.activeSpaceId === command.spaceId
            ? resolveAdjacentLayoutId(
                geometryDocument.spaces,
                command.spaceId,
              )
            : geometryDocument.activeSpaceId,
        layouts: nextLayouts,
        layoutsBySpaceId: nextLayoutsBySpaceId,
        viewportsBySpaceId: nextViewportsBySpaceId,
        blocks: nextBlocks,
        blockRecords: nextBlockRecords,
        // Codex P2-3 — `...geometryDocument` above re-spreads the ORIGINAL
        // rawEntityGroups; override with the purged array (same reference
        // when nothing was purged).
        ...(nextRawEntityGroups !== undefined
          ? { rawEntityGroups: nextRawEntityGroups }
          : {}),
      });
    }

    // ── LAYOUT RENAME (shell/rename-space) — sister-sweep persistence fix.
    // The shell arm has validated + renamed its projection since Phase
    // MC1, but the geometry DOCUMENT (the source of truth the resync
    // rebuilds `state.drawing.spaces` from, and the DXF export reads)
    // was never updated — the rename visually reverted on the very
    // dispatch that applied it and never reached the exported DXF.
    // Guards are the SHARED `validateRenameSpace` predicates (Model
    // protected, target exists + is a layout, non-empty, ≤255 chars,
    // case-INSENSITIVE duplicate — Codex P2-2/P2-4) also evaluated by
    // the shell arm, so the two dispatch arms accept/reject
    // identically. Rename the space + re-key the by-name LAYOUT
    // page-setup entry (LAYOUT objects are keyed by tab name; the by-id
    // entry keeps its key but carries the new name).
    case "shell/rename-space": {
      const trimmedName = command.newName.trim();
      if (
        validateRenameSpace(
          geometryDocument.spaces,
          command.spaceId,
          trimmedName,
        ) !== null
      ) {
        return geometryDocument;
      }
      const target = geometryDocument.spaces.find(
        (space) => space.id === command.spaceId,
      );
      if (!target) {
        return geometryDocument;
      }
      const renamedLayouts = { ...(geometryDocument.layouts ?? {}) };
      const renamedLayoutsBySpaceId = {
        ...(geometryDocument.layoutsBySpaceId ?? {}),
      };
      const byIdEntry = renamedLayoutsBySpaceId[command.spaceId];
      // Codex P2-1 (sister site) — resolve the renamed space's by-name
      // entry through the by-id binding, not by blind
      // `layouts[target.name]`: with duplicate imported LAYOUT names the
      // visible-name lookup misses (the by-name map is keyed by the RAW
      // name) and the old shared-key entry would be left behind — the
      // export then emitted BOTH the stale and the renamed entry for the
      // same LAYOUT handle.
      const byNameKey = findLayoutKeyForSpace(
        renamedLayouts,
        byIdEntry,
        target.name,
      );
      const byNameEntry =
        byNameKey !== undefined ? renamedLayouts[byNameKey] : undefined;
      // Codex P2 — seed tabs ("Layout 1"/"Layout 2") and legacy imports
      // synthesised only into `layoutsBySpaceId` carry NO by-name
      // `layouts` entry. Rename must MATERIALISE the LAYOUT page-setup
      // entry into BOTH maps; otherwise the by-name map the export reads
      // stays stale/absent and the exported LAYOUT object loses or
      // misnames the tab. Source priority: by-id entry (authoritative
      // for this space) → by-name entry → fresh `buildDefaultLayout`
      // (the same materialisation path as `shell/add-space` above —
      // synced flat plot mirrors, Codex P2-2 — tabOrder via the shared
      // `deriveNextTabOrder`).
      const renamedEntry: Layout = byIdEntry
        ? { ...byIdEntry, name: trimmedName }
        : byNameEntry
          ? { ...byNameEntry, name: trimmedName }
          : buildDefaultLayout(
              trimmedName,
              deriveNextTabOrder(geometryDocument),
            );
      if (byNameKey !== undefined) {
        delete renamedLayouts[byNameKey];
      }
      renamedLayouts[trimmedName] = renamedEntry;
      renamedLayoutsBySpaceId[command.spaceId] = renamedEntry;
      // Codex P2-4 (r5) — sister of the delete-path re-seed: when the
      // renamed duplicate's by-id entry OWNED the shared by-name key
      // ("Sheet"), the delete above removed the surviving same-named
      // sibling's only by-name entry — its LAYOUT object would vanish
      // from export. Re-seed it from its by-id entry (same helper as
      // `shell/remove-space`).
      reseedSurvivorByNameEntries(
        geometryDocument.spaces,
        command.spaceId,
        renamedLayoutsBySpaceId,
        renamedLayouts,
      );
      // Codex P2-2 (r5) — retag raw ENTITIES-section passthrough records
      // (unsupported entities, re-emitted verbatim by the writer) whose
      // group 410 still carries the renamed tab's OLD name: without the
      // retag the export emits records pointing at a tab name no LAYOUT
      // claims any more (dangling 410). Duplicate-name safe via the SAME
      // exclusive-name resolution as the delete purge: a shared old name
      // still claimed by a surviving sibling (visible name or its by-id
      // entry's raw name) belongs to the SIBLING's records — never
      // retagged. 67=1-no-410 records follow the default `*Paper_Space`
      // ownership, which a rename does not move — untouched.
      const renamedTabNames = collectExclusiveTabNames(
        geometryDocument,
        command.spaceId,
        [target.name, (byIdEntry ?? byNameEntry)?.name],
      );
      let renamedRawEntityGroups = geometryDocument.rawEntityGroups;
      if (renamedRawEntityGroups?.length && renamedTabNames.size > 0) {
        let retaggedAny = false;
        const retagged = renamedRawEntityGroups.map((record) => {
          const hasRenamedTab = record.groups.some(
            (group) =>
              group.code === 410 && renamedTabNames.has(group.value.trim()),
          );
          if (!hasRenamedTab) return record;
          retaggedAny = true;
          return {
            ...record,
            groups: record.groups.map((group) =>
              group.code === 410 && renamedTabNames.has(group.value.trim())
                ? { ...group, value: trimmedName }
                : group,
            ),
          };
        });
        if (retaggedAny) renamedRawEntityGroups = retagged;
      }
      return assembleDrawingDocument({
        ...geometryDocument,
        spaces: geometryDocument.spaces.map((space) =>
          space.id === command.spaceId
            ? { ...space, name: trimmedName }
            : space,
        ),
        layouts: renamedLayouts,
        layoutsBySpaceId: renamedLayoutsBySpaceId,
        // `...geometryDocument` above re-spreads the ORIGINAL
        // rawEntityGroups; override with the retagged array (same
        // reference when nothing was retagged).
        ...(renamedRawEntityGroups !== undefined
          ? { rawEntityGroups: renamedRawEntityGroups }
          : {}),
      });
    }

    case "geometry/create-viewport": {
      // MVIEW is LAYOUT-ONLY (AutoCAD): a Viewport entity may only be created in
      // a paper-space layout (DrawingSpace kind === "layout"), never in model
      // space, a block-edit / refedit session, or an orphan id. This is the
      // single fail-closed source of truth protecting EVERY dispatch path (CLI,
      // AI, UI) — without it a `spaceId:"model"` MVIEW would store a Viewport in
      // model space (Cleanup Q6b #276). An orphan id was already a no-op via
      // appendGeometryEntity's space-existence check; this also rejects "model"
      // and other non-layout kinds. No-op (return unchanged doc) on rejection.
      // The same predicate guards the shell reducer (#287-11) so the two paths
      // share one source of truth (`isPaperSpaceLayoutTarget`).
      if (!isPaperSpaceLayoutTarget(geometryDocument, command.spaceId)) {
        return geometryDocument;
      }
      const vc1 = command.corner1;
      const vc2 = command.corner2;
      const vpCenter = { x: (vc1.x + vc2.x) / 2, y: (vc1.y + vc2.y) / 2 };
      const vpWidth = Math.abs(vc2.x - vc1.x);
      const vpHeight = Math.abs(vc2.y - vc1.y);
      // Clamp widths to at least 1 to satisfy the schema's positive
      // constraint; aspect uses the clamped values so a degenerate
      // (zero) pick falls back to 1:1.
      const vpWidthSafe = Math.max(1, vpWidth);
      const vpHeightSafe = Math.max(1, vpHeight);
      const aspect = vpWidthSafe / vpHeightSafe;

      // Auto-fit the model-space view to the supplied extent, matching
      // AutoCAD's default MVIEW "FIT" behaviour. Legacy callers (no
      // extent, empty model) keep the previous (0, 0) / 100 defaults.
      const ext = command.modelExtent;
      const modelW = ext ? ext.maxX - ext.minX : 0;
      const modelH = ext ? ext.maxY - ext.minY : 0;
      let viewHeight = 100;
      let viewCenter: { x: number; y: number } = { x: 0, y: 0 };
      if (ext && modelW > 0 && modelH > 0) {
        const fitByH = modelH * 1.1;
        const fitByW = (modelW * 1.1) / aspect;
        viewHeight = Math.max(fitByH, fitByW);
        viewCenter = {
          x: (ext.minX + ext.maxX) / 2,
          y: (ext.minY + ext.maxY) / 2,
        };
      }

      return appendGeometryEntity(geometryDocument, {
        id: nextGeometryEntityId(geometryDocument, "viewport"),
        type: "Viewport",
        spaceId: command.spaceId,
        center: vpCenter,
        width: vpWidthSafe,
        height: vpHeightSafe,
        viewCenter,
        viewHeight,
        viewDirection: { x: 0, y: 0, z: 1 },
        viewTarget: { x: 0, y: 0, z: 0 },
        viewTwistAngle: 0,
        lensLength: 50,
        frontClipZ: 0,
        backClipZ: 0,
        viewportStatus: 1,
        displayLocked: false,
        frozenLayers: [],
        // T7 (agent-bridge) local Codex round-17 P2 — CLAYER stamped at the
        // dispatch boundary onto the new VIEWPORT frame entity (mirrors
        // every other stampable create's `command.layer ?? "0"` pattern).
        layer: command.layer ?? "0",
      });
    }

    default:
      return null;
  }
}
